import SimpleModal from 'components/SimpleModal';
import { FC, useEffect, useState } from 'react';
import { conditionallyRenderElement } from 'utils';
import { FileCopy as FileCopyIcon } from '@mui/icons-material';
import { Box, Button, Checkbox, FormControlLabel, TextField, Alert } from '@mui/material';
import { updateTeam } from 'thunk';
import { useDispatch, useSelector } from 'react-redux';
import { selectTeamHash } from 'selectors';
import { useGenerateTeamApiTokenQuery } from 'TeamModule/query/hooks/useGenerateTeamApiTokenQuery';
import { StandardTag } from '@constants';
import { useCurrentContext } from 'hooks/useCurrentContext';

export const GenerateApiTokenModal: FC<{
  onClose: VoidFunction;
  teamId: number;
}> = ({ onClose, teamId }) => {
  // Get context from URL params (with auth default fallback)
  const { tenant, realmId } = useCurrentContext();
  const dispatch = useDispatch();
  const teamHash = useSelector(selectTeamHash);
  const team = teamHash[teamId];
  const [hasCopiedToken, setHasCopiedToken] = useState(false);
  const [shouldTriggerFetchApiToken, setShouldTriggerFetchApiToken] = useState(false);
  const [apiTokenParams, setApiTokenParams] = useState({
    domain: '',
    callbackUrl: '',
    enableWebhooks: true,
  });

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      setApiTokenParams({
        ...apiTokenParams,
        [name]: e.target.checked,
      });
    } else {
      setApiTokenParams({
        ...apiTokenParams,
        [name]: value,
      });
    }
  };

  const {
    apiToken: teamApiToken,
    isFetchingApiToken,
    refetchApiToken,
    apiTokenError,
  } = useGenerateTeamApiTokenQuery({
    domain: apiTokenParams.domain.trim(),
    team_id: teamId,
    enable_webhooks: apiTokenParams.enableWebhooks,
    //waiting for BE to support this
    // callbackUrl: apiTokenParams.callbackUrl.trim(),
    enabled: shouldTriggerFetchApiToken,
    // Context headers (tenant/realmId) are automatically injected by useAppQuery
  });

  useEffect(() => {
    if (!team?.tags?.includes(StandardTag.ApiEnabled) && teamApiToken && teamId) {
      // TODO: Fix with Redux Toolkit configureStore migration
      dispatch(
        updateTeam({
          tags: [...(team?.tags || []), StandardTag.ApiEnabled],
          team_id: teamId,
          // Pass URL context for correct API headers
          tenant,
          realmId,
        }) as any,
      );
    }
  }, [dispatch, teamApiToken, team?.tags, teamId, tenant, realmId]);

  const fetchApiToken = () => {
    if (shouldTriggerFetchApiToken) {
      setShouldTriggerFetchApiToken(true);
      setHasCopiedToken(false);
    } else {
      void refetchApiToken();
    }
  };

  const copyToClipboard = () => {
    if (teamApiToken) {
      void navigator.clipboard.writeText(teamApiToken || '');
      setHasCopiedToken(true);
    }
  };

  return (
    <SimpleModal
      open
      onClose={onClose}
      headerText="Enter a domain to generate an API token (e.g. https://acme-consulting.com)"
      hideConfirmButton
      Body={
        <>
          <Box display={'flex'} flexDirection={'column'} alignItems={'end'}>
            <TextField
              fullWidth
              name="domain"
              label="Domain"
              required
              value={apiTokenParams.domain}
              onChange={onChange}
              helperText={
                "The domain is required to be used as the 'Origin' in API requests. When webhooks are enabled the callbacks will be sent to the URL '<domain>/webhook'. By default, Mosaic initiates callbacks using https requests. However, if the receiving service does not accept https, you should qualify the URL with http. See https://readme.mosaicapp.com/reference/authentication for more details."
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={apiTokenParams.enableWebhooks}
                  onChange={onChange}
                  name="enableWebhooks"
                />
              }
              label={'Enable Webhooks'}
            />
            {/* 
            //waiting for BE to support this
            <TextField
              fullWidth
              name="callbackUrl"
              label="Callback Url"
              value={apiTokenParams.callbackUrl}
              onChange={onChange}
            /> */}
            <Button
              style={{ marginTop: '1rem' }}
              onClick={fetchApiToken}
              variant="contained"
              disabled={!team}
              color="primary"
            >
              Generate
            </Button>
          </Box>
          <Box>
            {conditionallyRenderElement({
              isLoading: isFetchingApiToken,
              error: apiTokenError?.errors[0],
              content: (
                <>
                  {' '}
                  <h3>Token: {teamApiToken}</h3>
                  <FileCopyIcon
                    onClick={copyToClipboard}
                    style={{ cursor: teamApiToken ? 'pointer' : 'not-allowed' }}
                  />
                  <p>
                    Please ensure you copy the token, as it won&apos;t be accessible once this modal
                    is closed.
                  </p>
                </>
              ),
            })}
            {hasCopiedToken && <Alert severity="success">Copied!</Alert>}
          </Box>
        </>
      }
    />
  );
};
